import { Link, useLocation } from "react-router-dom";
import { UserCircle, Menu, LogIn, LogOut, ShieldCheck, ChevronDown } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useUser } from "@/contexts/UserContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ThemeToggle";

import { useState } from "react";

export const Header = () => {
  const location = useLocation();
  const { currentUser } = useUser();
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const { isAdmin } = useIsAdmin();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  type NavItem = { path: string; label: string };

  const mainNavLinks: NavItem[] = [
    { path: "/", label: t('nav.home') },
    { path: "/documents", label: "Documents" },
    { path: "/ai-assistant", label: t('nav.aiAssistant') },
    { path: "/risk-management", label: t('nav.riskManagement') },
    { path: "/audit-room", label: "Audit Room" },
    { path: "/tools", label: t('nav.tools') },
  ];

  const moreLinks = [
    { path: "/pricing", label: "Pricing" },
    { path: "/blog", label: t('nav.blog') },
    { path: "/iso-27001-annex-a-controls", label: "Annex A Controls" },
    { path: "/iso-27001-implementation-review", label: "Implementation Review" },
    { path: "/consultation", label: "Consultation" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
    { path: "/faq", label: t('faq.title') },
  ];

  const mobileNavLinks = [
    { path: "/", label: t('nav.home') },
    { path: "/documents", label: "Documents" },
    { path: "/audit-room", label: "Audit Room" },
    { path: "/ai-assistant", label: t('nav.aiAssistant') },
    { path: "/pricing", label: "Pricing" },
    { path: "/readiness-check", label: "Free readiness check" },
    { path: "/risk-management", label: t('nav.riskManagement') },
    { path: "/tools", label: t('nav.tools') },
    { path: "/blog", label: t('nav.blog') },
    { path: "/iso-27001-annex-a-controls", label: "Annex A Controls" },
    { path: "/iso-27001-implementation-review", label: "Implementation Review" },
    { path: "/consultation", label: "Consultation" },
    { path: "/about", label: "About" },
    { path: "/faq", label: t('faq.title') },
  ];

  const handleSignOut = async () => {
    await signOut();
    setMobileMenuOpen(false);
  };

  return (
    <header className="bg-header text-primary-foreground sticky top-0 z-50 shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
          {/* Logo with text - always on left */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0" aria-label="ISO27001KIT home">
            <span className="hidden sm:inline-flex">
              <BrandLogo height={56} variant="light" />
            </span>
            <span className="inline-flex sm:hidden">
              <BrandLogo height={44} variant="light" />
            </span>
          </Link>

          {/* Desktop Navigation - hidden on mobile/tablet */}
          <nav className="hidden lg:flex gap-1 xl:gap-2 2xl:gap-3 items-center">
            {mainNavLinks.map((link) => {
              const isActive =
                location.pathname === link.path ||
                (link.path !== "/" && location.pathname.startsWith(link.path + "/"));
              const baseClass = `relative text-sm font-medium hover:text-accent transition-colors whitespace-nowrap inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${
                isActive
                  ? "text-accent after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-0.5 after:bg-accent after:rounded-full"
                  : ""
              }`;
              return (
                <Link key={link.path} to={link.path} className={baseClass}>
                  {link.label}
                </Link>
              );
            })}
            <DropdownMenu>
              {(() => {
                const isMoreActive = moreLinks.some(
                  (l) =>
                    location.pathname === l.path ||
                    (l.path !== "/" && location.pathname.startsWith(l.path + "/")),
                );
                return (
                  <DropdownMenuTrigger
                    aria-label="More resources"
                    className={`relative text-sm font-medium hover:text-accent transition-colors whitespace-nowrap inline-flex items-center gap-1 px-2 py-1 rounded-md outline-none ${
                      isMoreActive
                        ? "text-accent after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-0.5 after:bg-accent after:rounded-full"
                        : ""
                    }`}
                  >
                    More resources <ChevronDown className="h-4 w-4" />
                  </DropdownMenuTrigger>
                );
              })()}
              <DropdownMenuContent align="end" className="bg-card border-border">
                {moreLinks.map((l) => (
                  <DropdownMenuItem asChild key={l.path}>
                    <Link
                      to={l.path}
                      className={`cursor-pointer ${
                        location.pathname === l.path ? "text-accent font-semibold" : ""
                      }`}
                    >
                      {l.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button asChild size="sm" className="hidden xl:inline-flex bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-3 ml-1 shadow-[0_2px_10px_hsl(var(--accent)/0.25)]">
              <Link to="/readiness-check">Free Readiness Check</Link>
            </Button>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="flex items-center gap-1.5 text-sm font-medium hover:text-accent transition-colors whitespace-nowrap outline-none"
                  aria-label={t('nav.account')}
                >
                  <UserCircle className="h-5 w-5" />
                  {currentUser.plan === 'pro' && (
                    <Badge className="bg-accent text-accent-foreground text-xs">Pro</Badge>
                  )}
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-border">
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer">
                      <UserCircle className="h-4 w-4 mr-2" />
                      {t('nav.account')}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin/subscriptions" className="cursor-pointer">
                        <ShieldCheck className="h-4 w-4 mr-2" />
                        Admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('nav.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/auth"
                className={`flex items-center gap-1.5 text-sm font-medium hover:text-accent transition-colors whitespace-nowrap px-2 py-1 rounded-md ${
                  location.pathname === "/auth" ? "text-accent" : ""
                }`}
              >
                <LogIn className="h-5 w-5" />
                <span className="hidden 2xl:inline">{t('nav.signIn')}</span>
              </Link>
            )}

            <ThemeToggle />
          </nav>

          {/* Mobile Hamburger Menu - shown on mobile and tablet */}
          <div className="lg:hidden flex items-center gap-1">
            <ThemeToggle />
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-primary-foreground" aria-label={t('nav.openMenu')}>
                  <Menu className="h-6 w-6" aria-hidden="true" />
                  <span className="sr-only">{t('nav.openMenu')}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-header text-primary-foreground border-l border-white/10 p-0 flex flex-col">
                <div className="px-6 pt-6 pb-4 border-b border-white/10">
                  <BrandLogo height={44} variant="light" />
                </div>
                <nav className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1">
                  {mobileNavLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-base font-medium hover:bg-white/5 hover:text-accent transition-colors py-2.5 px-3 rounded-md inline-flex items-center gap-2 ${
                        location.pathname === link.path ? "text-accent bg-white/5" : ""
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
                <div className="px-4 py-4 border-t border-white/10 flex flex-col gap-3">
                  <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold w-full shadow-[0_2px_10px_hsl(var(--accent)/0.25)]">
                    <Link to="/readiness-check" onClick={() => setMobileMenuOpen(false)}>Free Readiness Check</Link>
                  </Button>

                  {user ? (
                    <>
                      <Link
                        to="/account"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-2 text-sm font-medium hover:text-accent transition-colors py-2 px-3 rounded-md ${
                          location.pathname === "/account" ? "text-accent" : ""
                        }`}
                      >
                        <UserCircle className="h-4 w-4" />
                        <span>{t('nav.account')}</span>
                        {currentUser.plan === 'pro' && (
                          <Badge className="bg-accent text-accent-foreground">Pro</Badge>
                        )}
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-2 text-sm font-medium hover:text-accent transition-colors py-2 px-3 rounded-md text-left"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>{t('nav.signOut')}</span>
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/auth"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center justify-center gap-2 text-sm font-medium hover:text-accent transition-colors py-2 px-3 rounded-md border border-white/15 ${
                        location.pathname === "/auth" ? "text-accent" : ""
                      }`}
                    >
                      <LogIn className="h-4 w-4" />
                      <span>{t('nav.signIn')}</span>
                    </Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
